import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MessageCircle, Users, ChevronRight } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { getMyChats } from '../lib/api';
import { formatTimeAgo } from '../lib/utils';
import { toast } from 'sonner';
import BottomNav from '../components/BottomNav';

export default function ChatsPage() {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const loadChats = useCallback(async () => {
    try {
      const res = await getMyChats();
      setChats(res.data);
    } catch (error) {
      toast.error('Failed to load chats');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadChats();
  }, [loadChats]);

  if (loading) {
    return (
      <div className="min-h-screen bg-transparent">
        <div className="animate-pulse text-muted-foreground">Loading chats...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent">
      {/* Header */}
      <header className="sticky top-0 z-10 glass border-b border-border/50">
        <div className="p-4">
          <h1 className="text-2xl font-bold">Chats</h1>
          <p className="text-sm text-muted-foreground">Your event conversations</p>
        </div>
      </header>

      {chats.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-[60vh] p-6 text-center">
          <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-6">
            <MessageCircle className="w-10 h-10 text-muted-foreground" />
          </div>
          <h2 className="text-xl font-bold mb-2">No chats yet</h2>
          <p className="text-muted-foreground mb-6">Join an event to start chatting!</p>
        </div>
      ) : (
        <div className="divide-y divide-border">
          {chats.map((chat, i) => (
            <motion.button
              key={chat.event_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              data-testid={`chat-item-${chat.event_id}`}
              className="w-full flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors text-left"
              onClick={() => navigate(`/chat/${chat.event_id}`)}
            >
              <Avatar className="w-14 h-14">
                <AvatarImage src={chat.image_url} alt={chat.title} className="object-cover" />
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {chat.title.slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold truncate">{chat.title}</h3>
                {chat.last_message ? (
                  <p className="text-sm text-muted-foreground truncate">
                    {chat.last_message.sender_initials}: {chat.last_message.text}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">No messages yet</p>
                )}
              </div>

              <div className="flex flex-col items-end gap-1">
                {chat.last_message && (
                  <span className="text-xs text-muted-foreground">
                    {formatTimeAgo(chat.last_message.timestamp)}
                  </span>
                )}
                <div className="flex items-center gap-1 text-muted-foreground">
                  <Users className="w-3 h-3" />
                  <span className="text-xs">{chat.chat_members}</span>
                </div>
              </div>

              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </motion.button>
          ))}
        </div>
      )}

      <BottomNav />
    </div>
  );
}
